import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import { FormattedMessage } from 'react-intl'
import { Controller } from 'react-hook-form'
import { useSelector, useDispatch } from 'react-redux'
import { useIntl } from 'react-intl'
import { FormControl, InputLabel, MenuItem, Select, Tooltip } from '@mui/material'
import { selectTimeZones } from 'store/timeZones/slice'
import { getTimeZones } from 'store/timeZones/thunks'
import { convertTimeZoneToGmtOffset } from 'utils/timeZone'
import { rem } from 'theme/materialUITheme'

interface Props {
  name: string
  labelPosition?: 'top' | 'left'
  control: any
  required?: boolean
  value: number | string
  style?: React.CSSProperties
  disabled?: boolean
}

export default function TimeZoneSelect({
  name,
  labelPosition = 'top',
  control,
  value = '',
  required = false,
  style = {},
  disabled = false,
}: Props): JSX.Element {
  const intl = useIntl()
  const dispatch = useDispatch()
  const { timeZones, status, error } = useSelector(selectTimeZones)
  const [tooltipOpen, setTooltipOpen] = useState(false)
  const [selectOpen, setSelectOpen] = useState(false)

  useEffect(() => {
    if (timeZones.length === 0) {
      dispatch(getTimeZones())
    }
  }, [timeZones, dispatch])

  const getPlaceholderText = () => {
    if (required) {
      return intl.formatMessage({ id: 'strings.required' })
    }
    return intl.formatMessage({ id: 'strings.select' })
  }

  const getLabelWithValue = (value: any) => {
    const normalizedValue = Number(value)
    const timeZone = timeZones.find((timeZone: TimeZone) => Number(timeZone.id) === normalizedValue)
    return timeZone ? getLabel(timeZone) : ''
  }

  const getLabel = (timeZone: TimeZone) => {
    return `${convertTimeZoneToGmtOffset(timeZone)} ${timeZone.displayName}`
  }

  const ContainerVariant = labelPosition === 'left' ? LabelInputRowContainer : FormControl

  return (
    <Tooltip
      title={<FormattedMessage id="challenge.timeZone" />}
      placement="top"
      arrow
      enterDelay={300}
      open={tooltipOpen && !selectOpen}
      onOpen={() => setTooltipOpen(true)}
      onClose={() => setTooltipOpen(false)}
      slots={{
        tooltip: StyledToolTip,
      }}
    >
      <ContainerVariant margin="normal" fullWidth required={required} style={style}>
        <FieldLabel data-shrink="true">
          <FormattedMessage id="strings.timezone" />
        </FieldLabel>
        <Controller
          control={control}
          name={name}
          defaultValue={value ?? ''}
          render={({ field: { onChange, value: fieldValue } }) => (
            <StyledSelect
              error={error !== null}
              required={required}
              onChange={(e) => onChange(Number((e.target as HTMLInputElement).value))}
              variant="outlined"
              displayEmpty
              value={fieldValue ?? value ?? ''}
              open={selectOpen}
              onOpen={() => setSelectOpen(true)}
              onClose={() => setSelectOpen(false)}
              disabled={disabled || status === 'loading'}
              renderValue={(val: any) =>
                val !== undefined && val !== null && val !== '' ? (
                  getLabelWithValue(val)
                ) : (
                  <Placeholder>{getPlaceholderText()}</Placeholder>
                )
              }
            >
              {status === 'loading' && (
                <MenuItem value={0}>
                  <FormattedMessage id="strings.loading" />
                  ...
                </MenuItem>
              )}
              {timeZones.map((timeZone: TimeZone) => (
                <MenuItem key={timeZone.id} value={timeZone.id}>
                  {getLabel(timeZone)}
                </MenuItem>
              ))}
            </StyledSelect>
          )}
        />
      </ContainerVariant>
    </Tooltip>
  )
}

const LabelInputRowContainer = styled(FormControl)`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  width: 100%;
  margin: 5px 0 30px;

  & > label {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    position: relative;
    height: 40px;
    width: 100px;
    margin: 0 10px 0 0;
  }
  & > div {
    flex: 1;
  }
`

const FieldLabel = styled(InputLabel)`
  margin-top: -20px;
  transition: none;
  transform: none;
`

const Placeholder = styled.span`
  color: ${(props) => props.theme.customPalette.mediumGray};
`

const StyledSelect = styled(Select)`
  max-width: 100%;
  overflow: hidden;

  && {
    & .MuiSelect-select {
      padding-right: 25px;
    }
  }
  & .MuiOutlinedInput-root {
    background-color: #fff;
    border-radius: 8px;
    box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);

    & fieldset {
      border-color: #e4e4e7;
    }

    &:hover fieldset {
      border-color: #e4e4e7;
    }

    &.Mui-focused fieldset {
      border-color: #e4e4e7;
    }
  }

  & .MuiOutlinedInput-input {
    padding: 10px 12px;
  }

  & .MuiSelect-icon {
    color: #8a8a8e;
    font-size: 20px;
  }
`

const StyledToolTip = styled('div')`
  position: relative;
  background-color: #222a35;
  color: #ffffff;
  font-size: ${rem(16)};
  font-weight: 400;
  padding: 20px;
  border-radius: 10px;
  top: -8px;
  max-width: 360px;
`
